import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Header from './header/Header';
import { updateConsultationNote } from '../../../../../routes/doctor/consultation';
import getFormattedNote from '../../../../../util/consultation/getFormattedNote';
import { setIsNoteEditingPopupOpened } from '../../../../../redux/features/doctor/Dashboard/consultation/consultation';
import { generateNoteHtmlPreview, NOTE_STYLING_CONSTANTS } from '../../../../../util/consultation/noteFormattingUtils';
import { setEditableNoteRawText } from '../../../../../redux/features/doctor/Dashboard/consultation/consultationNote';

interface DocumentProps {
    isMobile: boolean;
}

function getNodePath(root: Node, node: Node): number[] {
    const path: number[] = [];
    let currentNode: Node | null = node;
    while (currentNode && currentNode !== root) {
        const parent = currentNode.parentNode;
        if (!parent) return [];
        path.unshift(Array.from(parent.childNodes).indexOf(currentNode as ChildNode));
        currentNode = parent;
    }
    return currentNode === root ? path : [];
}

function getNodeByPath(root: Node, path: number[]): Node | null {
    let currentNode: Node | null = root;
    for (const index of path) {
        if (!currentNode || !currentNode.childNodes[index]) return null;
        currentNode = currentNode.childNodes[index];
    }
    return currentNode;
}

const DOCTOR_SIGNATURE_MARKER = "#_______";

const Document: React.FC<DocumentProps> = ({ isMobile }) => {
    const dispatch = useAppDispatch();
    const { consultationNoteId } = useParams();

    const colors = useAppSelector(state => state.theme.colors);
    const securityPin = useAppSelector(state => state.consultation.securityPin);
    const descriptionFromRedux = useAppSelector(state => state.consultationNote.description);
    const editableNoteRawTextFromRedux = useAppSelector(state => state.consultationNote.editableNoteRawText);
    const doctorAccountInfo = useAppSelector(state => state.meta.accountInfo);


    const [localHtmlPreview, setLocalHtmlPreview] = useState('');
    const [timeLeftUntilRefreshing, setTimeLeftUntilRefreshing] = useState(0);
    const [isEditingPopupWarningShown, setIsEditingPopupWarningShown] = useState(false);

    const editorRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(150); // Initial estimate

    const isProgrammaticChangeRef = useRef(false);
    const lastKnownSelectionRef = useRef<{ anchorPath: number[], anchorOffset: number, focusPath: number[], focusOffset: number } | null>(null);
    const currentNoteIdRef = useRef<string | null>(null);
    const selfTriggeredDescriptionUpdateRef = useRef(false);

    // Effect to measure Header height
    useEffect(() => {
        const headerElement = document.getElementById('consultation-note-header');
        if (headerElement) {
            const resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    setHeaderHeight(entry.contentRect.height);
                }
            });
            resizeObserver.observe(headerElement);
            setHeaderHeight(headerElement.offsetHeight);
            return () => resizeObserver.disconnect();
        }
    }, []);

    useEffect(() => {
        let baseText = getFormattedNote(descriptionFromRedux);
        let textWithSignature = baseText;

        if (doctorAccountInfo && doctorAccountInfo.firstName && doctorAccountInfo.lastName) {
            const signaturePattern = `\n\n______________________\n${doctorAccountInfo.lastName} ${doctorAccountInfo.firstName} ${DOCTOR_SIGNATURE_MARKER}`;

            // Check if the signature marker is present
            if (!baseText.includes(DOCTOR_SIGNATURE_MARKER)) {
                 // If marker is NOT present, append the full signature pattern
                // Add a newline before the signature if the base text doesn't end with one
                if (baseText.length > 0 && !baseText.endsWith('\n')) {
                    textWithSignature = baseText + '\n' + signaturePattern;
                } else {
                    textWithSignature = baseText + signaturePattern;
                }
            } else {
                // If marker IS present, check if the name matches and update if necessary
                const currentSignatureRegex = new RegExp(`\n\n______________________\n(.*?) (.*?) ${DOCTOR_SIGNATURE_MARKER}`);
                const match = baseText.match(currentSignatureRegex);
                if (match && (match[1] !== doctorAccountInfo.lastName || match[2] !== doctorAccountInfo.firstName)) {
                    // Name mismatch, replace the old signature line with the new one
                    textWithSignature = baseText.replace(currentSignatureRegex, signaturePattern);
                } else if (!match) {
                    // Marker is present but pattern doesn't match (e.g., user edited the line heavily)
                    // In this case, we might decide to re-append the correct signature or leave it as is.
                    // Re-appending might be disruptive. Leaving it as is seems safer for user edits.
                    // For now, we'll just leave textWithSignature as baseText if the marker is there but the pattern is broken.
                    // A more sophisticated approach might try to find the marker and replace just the name part.
                    // Let's stick to the simpler approach: if marker is there, assume the line is user-managed unless name clearly differs in the expected pattern.
                    // The current logic already handles the name update if the pattern matches. If it doesn't match, we leave baseText as is.
                     textWithSignature = baseText; // Keep the user-edited version if pattern is broken
                } else {
                    // Marker is present and name matches, keep the existing text (no change needed)
                     textWithSignature = baseText;
                }
            }
        }


        if (consultationNoteId !== currentNoteIdRef.current) {
            dispatch(setEditableNoteRawText(textWithSignature));
            currentNoteIdRef.current = consultationNoteId || null;
            setIsEditingPopupWarningShown(false);
            selfTriggeredDescriptionUpdateRef.current = false;
        } else if (textWithSignature !== editableNoteRawTextFromRedux) {
            if (!selfTriggeredDescriptionUpdateRef.current) {
                dispatch(setEditableNoteRawText(textWithSignature));
            }
            selfTriggeredDescriptionUpdateRef.current = false;
        }
    }, [descriptionFromRedux, consultationNoteId, dispatch, editableNoteRawTextFromRedux, doctorAccountInfo]);


    useEffect(() => {
        const newHtml = generateNoteHtmlPreview(editableNoteRawTextFromRedux);
        if (newHtml !== localHtmlPreview) {
            isProgrammaticChangeRef.current = true;
            setLocalHtmlPreview(newHtml);
        }
    }, [editableNoteRawTextFromRedux, localHtmlPreview]);

    useEffect(() => {
        if (editorRef.current && isProgrammaticChangeRef.current) {
            const selection = window.getSelection();
            let anchorPath: number[] | null = lastKnownSelectionRef.current?.anchorPath || null;
            let anchorOffset: number = lastKnownSelectionRef.current?.anchorOffset || 0;
            let focusPath: number[] | null = lastKnownSelectionRef.current?.focusPath || null;
            let focusOffset: number = lastKnownSelectionRef.current?.focusOffset || 0;

            if (!lastKnownSelectionRef.current && selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                if (editorRef.current.contains(range.startContainer) && editorRef.current.contains(range.endContainer)) {
                    anchorPath = getNodePath(editorRef.current, range.startContainer);
                    anchorOffset = range.startOffset;
                    focusPath = getNodePath(editorRef.current, range.endContainer);
                    focusOffset = range.endOffset;
                }
            }

            editorRef.current.innerHTML = localHtmlPreview;
            isProgrammaticChangeRef.current = false;
            lastKnownSelectionRef.current = null;

            if (anchorPath && focusPath && selection) {
                const newAnchorNode = getNodeByPath(editorRef.current, anchorPath);
                const newFocusNode = getNodeByPath(editorRef.current, focusPath);
                if (newAnchorNode && newFocusNode) {
                    try {
                        const newRange = document.createRange();
                        const safeAnchorOffset = Math.min(anchorOffset, newAnchorNode.textContent?.length ?? 0);
                        const safeFocusOffset = Math.min(focusOffset, newFocusNode.textContent?.length ?? 0);
                        newRange.setStart(newAnchorNode, safeAnchorOffset);
                        newRange.setEnd(newFocusNode, safeFocusOffset);
                        selection.removeAllRanges();
                        selection.addRange(newRange);
                    } catch (e) { /* console.warn("Failed to restore selection", e); */ }
                }
            } else if (selection && editorRef.current.childNodes.length > 0) {
                const range = document.createRange();
                range.selectNodeContents(editorRef.current);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    }, [localHtmlPreview]);


    const getRawTextFromEditor = (editorElement: HTMLDivElement): string => {
        const lines: string[] = [];
        // Check if the first child is the main content div structure
        const mainContentDiv = editorElement.firstChild;
        if (mainContentDiv && mainContentDiv.nodeType === Node.ELEMENT_NODE && mainContentDiv.nodeName === 'DIV') {
             // Iterate through the line divs
            mainContentDiv.childNodes.forEach(lineDivNode => {
                if (lineDivNode.nodeType === Node.ELEMENT_NODE && lineDivNode.nodeName === 'DIV') {
                    const lineDiv = lineDivNode as HTMLElement;
                    // Check if the line is just a <br> (empty line)
                    if (lineDiv.innerHTML.trim().toLowerCase() === '<br>') {
                        lines.push("");
                    } else {
                        // Use textContent to get plain text, preserving internal whitespace
                        lines.push(lineDiv.textContent || "");
                    }
                } else if (lineDivNode.nodeType === Node.TEXT_NODE) {
                    // Handle direct text nodes within the main div if any (less common with our HTML structure)
                     lines.push(lineDivNode.textContent || "");
                }
            });
        } else if (editorElement.innerHTML === "" || editorElement.innerHTML.trim().toLowerCase() === "<br>" || editorElement.innerHTML.trim().toLowerCase() === "<div><br></div>") {
            // Handle completely empty or just a single <br> case
            return "";
        } else {
            // Fallback for unexpected structures, try to preserve content
            // This might happen if content is pasted from external sources with complex HTML
            // A more robust solution might involve sanitizing pasted HTML or using a library for HTML to text conversion.
            // For now, innerText is a reasonable fallback.
            console.warn("getRawTextFromEditor: Unexpected editor structure. Falling back to innerText. HTML:", editorElement.innerHTML);
            return editorElement.innerText;
        }
         // Join lines, ensuring empty lines are preserved by the split/push logic above
        return lines.join('\n');
    };


    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
        if (isProgrammaticChangeRef.current) {
            return;
        }

        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && editorRef.current) {
            const range = selection.getRangeAt(0);
            if (editorRef.current.contains(range.startContainer) && editorRef.current.contains(range.endContainer)) {
                lastKnownSelectionRef.current = {
                    anchorPath: getNodePath(editorRef.current, range.startContainer),
                    anchorOffset: range.startOffset,
                    focusPath: getNodePath(editorRef.current, range.endContainer),
                    focusOffset: range.endOffset,
                };
            }
        }

        const currentText = getRawTextFromEditor(e.currentTarget);
        if (currentText !== editableNoteRawTextFromRedux) {
            selfTriggeredDescriptionUpdateRef.current = true;
            dispatch(setEditableNoteRawText(currentText));
        }

        setTimeLeftUntilRefreshing(3);

        if (consultationNoteId && securityPin) {
            const content = {
                consultationNoteId: consultationNoteId,
                securityPin: securityPin,
                description: currentText,
                blankDescription: currentText.trim().length === 0,
            };
            updateConsultationNote(content);
        }

        if (!isEditingPopupWarningShown) {
            dispatch(setIsNoteEditingPopupOpened(true));
            setIsEditingPopupWarningShown(true);
        }
    };

    useEffect(() => {
        let myInterval: NodeJS.Timeout | null = null;
        if (timeLeftUntilRefreshing > 0) {
            myInterval = setInterval(() => {
                setTimeLeftUntilRefreshing(prevTime => {
                    if (prevTime <= 1) {
                        if(myInterval) clearInterval(myInterval);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        }
        return () => {
            if (myInterval) clearInterval(myInterval);
        };
    }, [timeLeftUntilRefreshing]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Tab') {
            event.preventDefault();
        }
    };

    // Use the dynamically measured headerHeight
    const editorMarginTop = `${headerHeight}px`;
    const editorHeight = `calc(100% - ${headerHeight}px)`;


    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            backgroundColor: colors?.container,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            boxSizing: 'border-box',
            flexShrink: 0,
            position: 'relative',
            ...(isMobile ? {
                width: '100%',
                height: 'auto',
                minHeight: '500px',
                margin: '8px 0 0 0',
            } : {
                width: '48.5%',
                height: '98%',
                position: 'absolute',
                bottom: '1%',
                right: '0.5%',
                margin: 0,
            })
        }}>
            {/* Wrap Header in a Box with the ID for measurement */}
            <Box id="consultation-note-header" sx={{width: '100%', position: 'absolute', top: 0, zIndex: 10}}>
                 <Header />
            </Box>

            <Box
                ref={editorRef}
                contentEditable={true}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                suppressContentEditableWarning={true}
                sx={{
                    width: '100%',
                    height: editorHeight, // Use dynamic height
                    marginTop: editorMarginTop, // Use dynamic margin
                    overflowY: 'auto',
                    padding: isMobile ? '10px' : '15px 25px',
                    boxSizing: 'border-box',
                    backgroundColor: colors?.background || '#ffffff',
                    border: `1px solid ${colors?.inputBorder || 'rgb(200,200,200)'}`,
                    borderRadius: 1,
                    fontFamily: NOTE_STYLING_CONSTANTS.FONT_FAMILY,
                    fontSize: `${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX}px`,
                    color: NOTE_STYLING_CONSTANTS.COLOR_TEXT_BODY_CSS,
                    lineHeight: `${NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}`,
                    textAlign: 'left',
                    outline: 'none',
                    minHeight: '100px',
                    '&:focus': {
                        borderColor: colors?.inputBorder || 'rgb(200,200,200)',
                        boxShadow: 'none',
                    },
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)', borderRadius: '4px', marginBlock: '2px' },
                    '&::-webkit-scrollbar-thumb': { background: colors?.main || '#888', borderRadius: '4px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: colors?.mainHover || '#555' }
                }}
            />
        </Box>
    );
};

export default Document;